// AdminCostControl.jsx — operational cost + control dashboard. Lives
// at /admin-cost-control. Polls /worker/admin/overview every 5s and
// shows:
//   - Per-brand $ spend & API-call counts (30-day window)
//   - Per-stage cost breakdown by model
//   - Queue inventory (waiting/active/failed per queue)
//   - Schedulers (interval, last run, next run, paused)
//   - Recent AI calls (last 100, with $ and kind)
//   - Per-brand drill-in: jobs in flight per queue + Cancel button
//
// All cancel actions hit /worker/admin/cancel-by-brand which removes
// in-flight jobs and flips Posts/Ideas to queueStatus='cancelled'.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";
import { get, post } from "../../api";
import LlmCostDashboard from "./LlmCostDashboard";

const STAGE_FLOW = [
  { key: 'idea',   label: 'Ideas',    countKey: null },
  { key: 'post',   label: 'Drafts',   countKey: null },
  { key: 'image',  label: 'Images',   countKey: null },
  { key: 'email',  label: 'Approval', countKey: null },
  { key: 'social', label: 'Posting',  countKey: null },
];

// Event types the admin form is allowed to fire. MUST stay in sync with
// EMITTABLE_EVENT_TYPES in /worker/routes/index.js — the server validates
// against its own whitelist, so adding here without adding there yields a 400.
const EMITTABLE_EVENTS = [
  { type: 'IDEAS_REQUESTED',            label: 'Force idea generation (1 brand)', defaultPayload: '{"count":1}' },
  { type: 'BRAND_ENRICHMENT_REQUESTED', label: 'Re-run brand enrichment',          defaultPayload: '{"reason":"manual"}' },
  { type: 'ENGAGEMENT_REFRESH_REQUESTED', label: 'Refresh engagement metrics',     defaultPayload: '{}' },
  { type: 'TOKEN_REFRESH_REQUESTED',    label: 'Force OAuth token refresh',         defaultPayload: '{}' },
];

// Small bar chart that renders the cost-timeseries response. Stacked
// bars per AI call kind (text/image/video) on a daily axis. Tooltip
// shows the per-kind breakdown plus the total.
function SpendChart({ series, height = 200, compact = false }) {
  if (!series || series.length === 0) {
    return <div style={{ color: '#bbb', fontSize: 12, padding: '12px 0' }}>No spend data in window.</div>;
  }
  const trimDay = (d) => (d || '').slice(5); // MM-DD
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={series} margin={{ top: 8, right: 8, left: compact ? -20 : 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" />
        <XAxis dataKey="day" tickFormatter={trimDay} tick={{ fontSize: 10 }} />
        <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => '$' + (v < 1 ? Number(v).toFixed(2) : v)} />
        <Tooltip
          formatter={(v, name) => ['$' + Number(v).toFixed(4), name]}
          labelStyle={{ fontSize: 11 }}
          contentStyle={{ fontSize: 11 }}
        />
        {!compact && <Legend wrapperStyle={{ fontSize: 11 }} />}
        <Bar dataKey="text"  stackId="cost" fill="#0F62FE" name="text" />
        <Bar dataKey="image" stackId="cost" fill="#42BE65" name="image" />
        <Bar dataKey="video" stackId="cost" fill="#F1C21B" name="video" />
      </BarChart>
    </ResponsiveContainer>
  );
}

// Inline editor: shows current interval in seconds + a Save button.
// Calls /worker/schedulers/:name/set-interval with the new ms value.
function SchedulerIntervalEdit({ name, currentMs, onSaved, onError }) {
  const [val, setVal] = useState(() => currentMs ? Math.round(currentMs / 1000) : '');
  // Re-sync local state when the parent re-fetches and current
  // changes upstream (e.g., a different scheduler hot-reloaded).
  useEffect(() => {
    setVal(currentMs ? Math.round(currentMs / 1000) : '');
  }, [currentMs]);
  const save = async () => {
    const seconds = parseInt(val, 10);
    if (!Number.isFinite(seconds) || seconds < 1) return onError && onError('invalid interval');
    try {
      await post(`/worker/schedulers/${name}/set-interval`, { intervalMs: seconds * 1000 }, false);
      if (onSaved) onSaved();
    } catch (e) { if (onError) onError((e && e.message) || 'set-interval failed'); }
  };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <input
        type="number"
        min={1}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        style={{ width: 70, padding: '2px 6px', border: '1px solid #ddd', borderRadius: 4, fontSize: 12 }}
        title="Interval in seconds"
      />
      <span style={{ fontSize: 11, color: '#888' }}>s</span>
      <button
        type="button"
        onClick={save}
        style={{ fontSize: 11, padding: '2px 8px', border: '1px solid #ddd', borderRadius: 4, background: '#fff', cursor: 'pointer' }}
        disabled={!val || (currentMs && parseInt(val, 10) * 1000 === currentMs)}
      >
        Save
      </button>
    </span>
  );
}

// QueueControlPanel — collapsible per-queue view: waiting/active/delayed/
// failed/completed sub-tables with per-job Retry/Remove/Promote buttons,
// an Add-Job inline form, and Pause/Resume/Drain/Clean controls.
//
// Polls /admin/queue/:name/jobs?state=… on a 4s interval while open.
// Closed panels don't poll — keeps the dashboard cheap when only one
// queue is being inspected at a time.
function QueueControlPanel({ queueName, totals, isSuperAdmin, onActionDone, onError }) {
  const [open, setOpen] = useState(false);
  const [stateFilter, setStateFilter] = useState('failed');
  const [jobs, setJobs] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState('manual');
  const [addData, setAddData] = useState('{}');
  const [addDelay, setAddDelay] = useState(0);
  const [addBrand, setAddBrand] = useState('');

  const fetchJobs = useCallback(async () => {
    if (!open) return;
    try {
      const r = await get(`/worker/admin/queue/${queueName}/jobs?state=${stateFilter}&limit=20`);
      setJobs(((r && r.data && r.data.jobs) || []));
    } catch (e) { onError && onError((e && e.message) || 'fetch jobs failed'); }
  }, [open, queueName, stateFilter, onError]);

  useEffect(() => {
    if (!open) return;
    fetchJobs();
    const id = setInterval(fetchJobs, 4000);
    return () => clearInterval(id);
  }, [open, fetchJobs]);

  const callAction = useCallback(async (url, body, label, jobId) => {
    setBusyId(jobId || url);
    try {
      await post(url, body || {}, false);
      onActionDone && onActionDone(`✓ ${label}`);
      fetchJobs();
    } catch (e) {
      onError && onError(`✗ ${label}: ${(e && e.message) || 'failed'}`);
    } finally { setBusyId(null); }
  }, [onActionDone, onError, fetchJobs]);

  const submitAddJob = async () => {
    let data;
    try { data = addData.trim() ? JSON.parse(addData) : {}; }
    catch { onError && onError('✗ data is not valid JSON'); return; }
    const body = { name: addName, data, delayMs: Number(addDelay) || 0 };
    if (addBrand) body.brandId = addBrand;
    await callAction(`/worker/admin/queue/${queueName}/add-job`, body, `added job to ${queueName}`);
    setAddOpen(false);
  };

  // Inline pill summary visible even when closed.
  const summary = totals ? (
    <span style={{ fontSize: 11, color: '#525252' }}>
      W:{totals.waiting || 0} A:{totals.active || 0} D:{totals.delayed || 0}
      {totals.failed > 0 ? <span style={{ color: '#c0392b', marginLeft: 4 }}>F:{totals.failed}</span> : null}
    </span>
  ) : null;

  return (
    <div style={{ border: '1px solid #eee', borderRadius: 6, marginBottom: 8 }}>
      <div
        onClick={() => setOpen((v) => !v)}
        style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', background: open ? '#f7f9fc' : '#fff', borderBottom: open ? '1px solid #eee' : 'none' }}
      >
        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{open ? '▾' : '▸'} {queueName}</span>
        {summary}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
          {isSuperAdmin ? (
            <>
              <button className="acc-btn" disabled={busyId} onClick={() => callAction(`/worker/admin/queue/${queueName}/pause`, {}, `paused ${queueName}`)}>Pause</button>
              <button className="acc-btn" disabled={busyId} onClick={() => callAction(`/worker/admin/queue/${queueName}/resume`, {}, `resumed ${queueName}`)}>Resume</button>
              <button className="acc-btn" disabled={busyId} onClick={() => { if (window.confirm(`Drain waiting + delayed from ${queueName}?`)) callAction(`/worker/admin/queue/${queueName}/drain`, { includeDelayed: true }, `drained ${queueName}`); }}>Drain</button>
              <button className="acc-btn" disabled={busyId} onClick={() => callAction(`/worker/admin/queue/${queueName}/clean?status=failed`, {}, `cleaned failed in ${queueName}`)}>Clean failed</button>
            </>
          ) : (
            <span className="text-muted" style={{ fontSize: 10 }} title="Queue-wide pause/drain/clean affects every company">queue-level controls: super only</span>
          )}
        </span>
      </div>
      {open && (
        <div style={{ padding: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
            {['failed','waiting','active','delayed','completed'].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStateFilter(s)}
                style={{
                  fontSize: 11, padding: '2px 10px', borderRadius: 12, cursor: 'pointer',
                  border: stateFilter === s ? '1px solid #0F62FE' : '1px solid #ddd',
                  background: stateFilter === s ? '#0F62FE' : '#fff',
                  color:  stateFilter === s ? '#fff' : '#525252',
                }}
              >
                {s} ({totals ? (totals[s === 'waiting' ? 'waiting' : s] || 0) : '?'})
              </button>
            ))}
            <button className="acc-btn" style={{ marginLeft: 'auto' }} onClick={() => setAddOpen((v) => !v)}>{addOpen ? 'Cancel add-job' : '+ Add job'}</button>
          </div>
          {addOpen && (
            <div style={{ border: '1px dashed #ddd', borderRadius: 6, padding: 8, marginBottom: 8, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'flex-start' }}>
              <input type="text"   value={addName}  onChange={(e) => setAddName(e.target.value)}  placeholder="job name"   style={{ fontSize: 12, padding: '4px 6px', border: '1px solid #ddd', borderRadius: 4, width: 140 }} />
              <input type="text"   value={addBrand} onChange={(e) => setAddBrand(e.target.value)} placeholder="brandId (opt)" style={{ fontSize: 12, padding: '4px 6px', border: '1px solid #ddd', borderRadius: 4, width: 200 }} />
              <input type="number" value={addDelay} onChange={(e) => setAddDelay(e.target.value)} placeholder="delay ms"   style={{ fontSize: 12, padding: '4px 6px', border: '1px solid #ddd', borderRadius: 4, width: 110 }} />
              <textarea value={addData} onChange={(e) => setAddData(e.target.value)} rows={2} placeholder='data JSON {"brandId":"…","postId":"…"}' style={{ flex: '1 1 220px', minWidth: 220, fontSize: 12, padding: 6, border: '1px solid #ddd', borderRadius: 4, fontFamily: 'monospace' }} />
              <button className="acc-btn acc-btn-primary" onClick={submitAddJob}>Add</button>
            </div>
          )}
          {jobs.length === 0 && <div style={{ color: '#bbb', fontSize: 12, padding: '6px 0' }}>No {stateFilter} jobs.</div>}
          {jobs.length > 0 && (
            <table className="acc-table">
              <thead>
                <tr><th>Job ID</th><th>Name</th><th>Data</th><th>Attempts</th><th>When</th><th>Last error</th><th></th></tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={j.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{j.id}</td>
                    <td>{j.name}</td>
                    <td style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11 }} title={JSON.stringify(j.data)}>{JSON.stringify(j.data)}</td>
                    <td>{j.attempts}/{j.maxAttempts}</td>
                    <td className="text-muted" style={{ fontSize: 11 }}>{fmtRel(j.timestamp)}</td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#c0392b', fontSize: 11 }} title={j.failedReason || ''}>{j.failedReason || '—'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {stateFilter === 'failed' && (
                        <button className="acc-btn" disabled={busyId === j.id} onClick={() => callAction(`/worker/admin/job/${queueName}/${j.id}/retry`, {}, `retried ${j.id}`, j.id)}>Retry</button>
                      )}
                      {stateFilter === 'delayed' && (
                        <button className="acc-btn" disabled={busyId === j.id} onClick={() => callAction(`/worker/admin/job/${queueName}/${j.id}/promote`, {}, `promoted ${j.id}`, j.id)}>Promote</button>
                      )}
                      <button className="acc-btn acc-btn-danger" style={{ marginLeft: 4 }} disabled={busyId === j.id} onClick={() => { if (window.confirm(`Remove job ${j.id}?`)) callAction(`/worker/admin/job/${queueName}/${j.id}/remove`, {}, `removed ${j.id}`, j.id); }}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function fmtUsd(n) {
  if (n == null) return '$0.00';
  return '$' + Number(n).toFixed(n < 1 ? 4 : 2);
}
function fmtTime(t) {
  if (!t) return '—';
  try { return new Date(t).toLocaleTimeString(); } catch { return ''; }
}
function fmtRel(t) {
  if (!t) return '—';
  const d = Date.now() - new Date(t).getTime();
  if (d < 60_000) return `${Math.floor(d/1000)}s ago`;
  if (d < 3600_000) return `${Math.floor(d/60_000)}m ago`;
  return `${Math.floor(d/3600_000)}h ago`;
}

export default function AdminCostControl() {
  // Super-admin only. USD spend, AI call counts, budgets, panic
  // kill-switch — tenant admins must never see raw provider cost.
  // App.jsx already gates the route, but a hard-coded URL or a
  // promoted-then-demoted user could land here with stale state, so
  // we redirect at the component level as a second line of defense.
  const userInfoRedux = useSelector((s) => s.user.userInfo);
  const isSuperAdmin   = !!userInfoRedux?.superAdmin;
  // Defense-in-depth redirect is rendered at the bottom of the JSX
  // tree (not as an early return) so all hooks below run in the same
  // order on every render — moving this above hooks tripped
  // react-hooks/rules-of-hooks.
  const isCompanyAdmin = false; // companyAdmin path retired — page is super-admin only

  const [data, setData] = useState(null);
  const [brandDetail, setBrandDetail] = useState(null);
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const [error, setError] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);
  // Track which recent-call rows have their input/output expanded
  // so the user can click to see the full snippet.
  const [expandedCall, setExpandedCall] = useState({});

  // Cost-over-time series. Polled less often than the overview because
  // it's a heavier aggregation and the data is per-day, not per-second.
  const [timeseries, setTimeseries] = useState(null);
  const [brandSeries, setBrandSeries] = useState(null);
  const [tsDays, setTsDays] = useState(30);

  // Super-admin "filter by company" dropdown. When set, every cost
  // endpoint is called with ?company=<id>. The page also renders both
  // a TRUE cost (raw, what we actually pay) AND a WITH MARKUP cost
  // (what the customer sees, raw × multiplier) so the operator can
  // sanity-check the customer view + the margin at a glance.
  // null = "All companies" (org-wide, default).
  const [companyFilter, setCompanyFilter] = useState(null);
  const [companies, setCompanies] = useState([]);   // [{ _id, name }]
  const [markup, setMarkup] = useState(1.6);         // platform multiplier; fetched once on mount

  // Manual event-emit form state. Lives inline above the schedulers card.
  const [emitEvt, setEmitEvt] = useState(EMITTABLE_EVENTS[0].type);
  const [emitBrandId, setEmitBrandId] = useState('');
  const [emitPayload, setEmitPayload] = useState(EMITTABLE_EVENTS[0].defaultPayload);
  const [emitBusy, setEmitBusy] = useState(false);

  // Helper: tack ?company=<id> onto every cost endpoint when the
  // super-admin has picked one from the dropdown. The worker proxy
  // already forwards the query string; the worker routes already
  // accept it for tenant scoping when the caller is super.
  const companyParam = companyFilter ? `&company=${encodeURIComponent(companyFilter)}` : '';
  const companyParamLead = companyFilter ? `?company=${encodeURIComponent(companyFilter)}` : '';

  const fetchOverview = useCallback(async () => {
    try {
      const r = await get(`/worker/admin/overview${companyParamLead}`);
      setData(r && r.data ? r.data : r);
      setError(null);
    } catch (e) { setError((e && e.message) || 'Failed'); }
  }, [companyParamLead]);
  const fetchBrand = useCallback(async (bid) => {
    if (!bid) { setBrandDetail(null); return; }
    try {
      const r = await get(`/worker/admin/brand/${bid}`);
      setBrandDetail(r && r.data ? r.data : r);
    } catch (e) { setError((e && e.message) || 'Failed'); }
  }, []);
  const fetchTimeseries = useCallback(async (days) => {
    try {
      const r = await get(`/worker/admin/cost-timeseries?days=${days || tsDays}${companyParam}`);
      const payload = r && r.data ? r.data : r;
      setTimeseries(payload);
    } catch (e) { /* non-fatal; chart hides */ }
  }, [tsDays, companyParam]);
  const fetchBrandSeries = useCallback(async (bid) => {
    if (!bid) { setBrandSeries(null); return; }
    try {
      const r = await get(`/worker/admin/cost-timeseries?days=${tsDays}&brandId=${bid}`);
      const payload = r && r.data ? r.data : r;
      setBrandSeries(payload);
    } catch (e) { setBrandSeries(null); }
  }, [tsDays]);

  useEffect(() => {
    fetchOverview();
    const id = setInterval(fetchOverview, 5000);
    return () => clearInterval(id);
  }, [fetchOverview]);

  // One-shot: load the company list + effective markup for the
  // super-admin's filter dropdown + dual cost display. Skips silently
  // for non-super-admin (the endpoints 403).
  useEffect(() => {
    if (!isSuperAdmin) return;
    (async () => {
      try {
        const [c, m] = await Promise.all([
          get('/api/admin/llm-budget/companies'),
          get('/api/admin/llm-budget/markup'),
        ]);
        setCompanies((c && c.data) || []);
        if (m && m.data && typeof m.data.multiplier === 'number') setMarkup(m.data.multiplier);
      } catch (_) { /* dropdown stays empty; default markup used */ }
    })();
  }, [isSuperAdmin]);
  useEffect(() => {
    fetchTimeseries(tsDays);
    // Cost-over-time refresh every 60s — daily buckets don't move fast.
    const id = setInterval(() => fetchTimeseries(tsDays), 60_000);
    return () => clearInterval(id);
  }, [fetchTimeseries, tsDays]);
  useEffect(() => {
    if (!selectedBrandId) return;
    fetchBrand(selectedBrandId);
    fetchBrandSeries(selectedBrandId);
    const id = setInterval(() => fetchBrand(selectedBrandId), 5000);
    const id2 = setInterval(() => fetchBrandSeries(selectedBrandId), 60_000);
    return () => { clearInterval(id); clearInterval(id2); };
  }, [selectedBrandId, fetchBrand, fetchBrandSeries]);

  // When the user picks a different event type, swap the payload
  // textarea to that event's example so they don't have to type from scratch.
  const onEmitEvtChange = (t) => {
    setEmitEvt(t);
    const def = EMITTABLE_EVENTS.find((e) => e.type === t);
    if (def) setEmitPayload(def.defaultPayload || '{}');
  };
  const submitEmit = useCallback(async () => {
    if (!emitBrandId) { setActionMsg('✗ pick a brand first'); return; }
    let parsed;
    try { parsed = emitPayload.trim() ? JSON.parse(emitPayload) : {}; }
    catch { setActionMsg('✗ payload is not valid JSON'); return; }
    setEmitBusy(true);
    setActionMsg(null);
    try {
      const r = await post('/worker/admin/emit-event', {
        eventType: emitEvt, brandId: emitBrandId, payload: parsed,
      }, false);
      const dk = r && r.data && r.data.dedupeKey;
      setActionMsg(`✓ ${emitEvt} emitted${dk ? ` (${dk.slice(0, 40)}…)` : ''}`);
      setTimeout(() => setActionMsg(null), 5000);
      fetchOverview();
    } catch (e) {
      setActionMsg(`✗ ${(e && e.message) || 'emit failed'}`);
    } finally { setEmitBusy(false); }
  }, [emitEvt, emitBrandId, emitPayload, fetchOverview]);

  // Brand list for the emit dropdown — prefer the workflows list (all
  // live brands) and fall back to perBrand (only brands with activity).
  const brandOptions = useMemo(() => {
    const fromWf = data?.workflows?.[0]?.brands || [];
    if (fromWf.length) return fromWf.map((b) => ({ _id: b._id, name: b.name }));
    return (data?.perBrand || []).map((b) => ({ _id: b.brandId, name: b.brandName }));
  }, [data]);

  // Hit /admin/scheduler/:name/:verb — pause/resume/run-now.
  const schedulerAction = useCallback(async (name, verb) => {
    setActionMsg(null);
    try {
      await post(`/worker/admin/scheduler/${name}/${verb}`, {}, false);
      setActionMsg(`✓ ${name} ${verb}`);
      setTimeout(() => setActionMsg(null), 3000);
      fetchOverview();
    } catch (e) { setActionMsg(`✗ ${verb} failed: ${(e && e.message) || ''}`); }
  }, [fetchOverview]);

  const cancelBrand = useCallback(async (brandId, queueName) => {
    setActionMsg(null);
    try {
      const r = await post('/worker/admin/cancel-by-brand', { brandId, queueName }, false);
      const removed = (r && r.data && r.data.totalRemoved) || 0;
      setActionMsg(`✓ cancelled ${removed} job${removed === 1 ? '' : 's'}${queueName ? ` in ${queueName}` : ' across all queues'}`);
      setTimeout(() => setActionMsg(null), 4000);
      fetchOverview();
      if (selectedBrandId) fetchBrand(selectedBrandId);
    } catch (e) { setActionMsg(`✗ ${(e && e.message) || 'cancel failed'}`); }
  }, [fetchOverview, fetchBrand, selectedBrandId]);

  // Kill every in-flight job in a specific queue, regardless of brand.
  // Confirm dialog because this is a mass-cancel — easy to misclick.
  const cancelStage = useCallback(async (queueName) => {
    if (!window.confirm(`Cancel ALL in-flight jobs in "${queueName}"?\nThis removes waiting + active + delayed jobs from BullMQ. Cannot be undone.`)) return;
    setActionMsg(null);
    try {
      const r = await post('/worker/admin/cancel-stage', { queueName }, false);
      const removed = (r && r.data && r.data.removed) || 0;
      setActionMsg(`✓ cancelled ${removed} job${removed === 1 ? '' : 's'} in ${queueName}`);
      setTimeout(() => setActionMsg(null), 4000);
      fetchOverview();
    } catch (e) { setActionMsg(`✗ ${(e && e.message) || 'cancel-stage failed'}`); }
  }, [fetchOverview]);

  // Pulse stages when their (waiting+active) count changes upward
  // — visual hint that an item just moved through. Same pattern as
  // /automation-pipeline but compact.
  const prevQCountsRef = useRef({});
  const [stagePulse, setStagePulse] = useState({});
  useEffect(() => {
    if (!data || !data.queues) return;
    const newPulses = {};
    const prev = prevQCountsRef.current;
    for (const s of STAGE_FLOW) {
      const q = data.queues[s.key] || {};
      const cur = (q.waiting || 0) + (q.active || 0);
      const was = prev[s.key] || 0;
      if (cur > was) newPulses[s.key] = Date.now();
      prev[s.key] = cur;
    }
    if (Object.keys(newPulses).length) {
      setStagePulse((p) => ({ ...p, ...newPulses }));
      const t = setTimeout(() => setStagePulse({}), 1500);
      return () => clearTimeout(t);
    }
  }, [data]);

  const totalSpend = useMemo(() => {
    if (!data || !Array.isArray(data.perBrand)) return 0;
    return data.perBrand.reduce((s, b) => s + Number(b.totalUsd || 0), 0);
  }, [data]);

  // Defense-in-depth: even if a non-super user somehow lands here
  // (stale Redux, manual URL), bounce them. Placed AFTER all hooks
  // run so hook order is stable.
  if (!isSuperAdmin) return <Redirect to="/dashboard" />;

  return (
    <div className="container py-4">
      <style>{`
        .acc-card { background: #fff; border: 1px solid #e5e5e5; border-radius: 8px; padding: 14px; margin-bottom: 16px; }
        .acc-card h5 { font-size: 14px; color: #15172b; text-transform: uppercase; letter-spacing: .5px; margin-bottom: 10px; }
        .acc-table { width: 100%; font-size: 13px; }
        .acc-table th { font-size: 11px; color: #888; text-transform: uppercase; text-align: left; font-weight: 500; border-bottom: 1px solid #f1f1f1; padding: 6px 8px; }
        .acc-table td { padding: 6px 8px; border-bottom: 1px solid #f4f4f4; vertical-align: middle; }
        .acc-table tr:hover td { background: #fafbfc; }
        .acc-pill { display: inline-block; font-size: 11px; padding: 1px 8px; border-radius: 10px; }
        .acc-pill-warn { background: #fff4cc; color: #856404; }
        .acc-pill-danger { background: #fdecea; color: #a71d2a; }
        .acc-pill-ok { background: #e7f5ec; color: #1e6b3e; }
        .acc-cancel { font-size: 11px; padding: 2px 8px; border: 1px solid #c0392b; color: #c0392b; background: #fff; border-radius: 4px; cursor: pointer; }
        .acc-cancel:hover { background: #fdecea; }
        .acc-pick { color: #0F62FE; cursor: pointer; font-weight: 500; }
        .acc-pick:hover { text-decoration: underline; }
        .acc-top { display: flex; align-items: baseline; gap: 16px; margin-bottom: 12px; }
        .acc-top h3 { margin-right: auto; }
        .acc-call-row td { font-size: 12px; }
        .acc-call-row .input-out { color: #888; font-size: 11px; max-width: 280px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .acc-preview { color: #555; font-size: 11px; max-width: 360px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; cursor: pointer; }
        .acc-preview.expanded { white-space: pre-wrap; max-width: none; max-height: 180px; overflow: auto; background: #fafbfc; padding: 6px; border-radius: 4px; }
        .acc-flow-strip { display: flex; align-items: stretch; gap: 6px; padding: 10px 0; }
        .acc-flow-cell { flex: 1 1 0; background: #fff; border: 1px solid #e5e5e5; border-radius: 6px; padding: 8px 6px; text-align: center; transition: transform .25s, box-shadow .25s; }
        .acc-flow-cell.pulse { transform: scale(1.05); box-shadow: 0 4px 12px rgba(15,98,254,.15); border-color: #0F62FE; }
        .acc-flow-label { font-size: 10px; color: #888; text-transform: uppercase; }
        .acc-flow-num { font-size: 20px; font-weight: 700; color: #15172b; line-height: 1; margin: 4px 0; }
        .acc-flow-arrow { display: flex; align-items: center; color: #ccc; font-size: 16px; padding: 0 2px; }
        .acc-btn { font-size: 11px; padding: 2px 8px; border: 1px solid #ddd; background: #fff; border-radius: 4px; cursor: pointer; color: #525252; }
        .acc-btn:hover:not(:disabled) { background: #f7f9fc; border-color: #bbb; }
        .acc-btn:disabled { opacity: .5; cursor: wait; }
        .acc-btn-primary { color: #fff; background: #0F62FE; border-color: #0F62FE; }
        .acc-btn-primary:hover:not(:disabled) { background: #0846b3; }
        .acc-btn-danger { color: #c0392b; border-color: #f1c4c0; }
        .acc-btn-danger:hover:not(:disabled) { background: #fdecea; }
        .acc-row-pulse { animation: accRowPulse 1.2s ease-out; }
        @keyframes accRowPulse { 0% { background: #fff8d6; } 100% { background: transparent; } }
      `}</style>

      <div className="acc-top">
        <h3>API Cost & Control</h3>
        {isCompanyAdmin && (
          <span className="acc-pill" style={{ background: '#eef2ff', color: '#15172b', fontSize: 11 }}>
            scoped to your company
          </span>
        )}

        {/* Super-admin company filter. Defaults to "All companies"
            (org-wide raw cost). Picking one filters every API cost
            call by that company AND shows both the true cost (raw)
            and the customer-facing cost (raw × markup multiplier). */}
        {isSuperAdmin && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="acc-pill" style={{ background: '#fff4cc', color: '#856404', fontSize: 11 }}>
              super admin
            </span>
            <select
              value={companyFilter || ''}
              onChange={(e) => setCompanyFilter(e.target.value || null)}
              title="Filter by company"
              style={{
                fontSize: 12, padding: '4px 10px', border: '1px solid #d1d5db',
                borderRadius: 4, background: '#fff', minWidth: 220,
                fontWeight: companyFilter ? 600 : 400,
              }}>
              <option value="">All companies (org-wide)</option>
              {companies.map((c) => (
                <option key={c._id} value={c._id}>{c.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Cost totals row. Super-admin filtering by company sees BOTH
            true cost (raw, what we pay) and with-markup (raw × multiplier,
            what the customer would see). All-companies mode and
            company-admin mode just show one number. */}
        <div className="text-muted" style={{ fontSize: 12, display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
          {isSuperAdmin && companyFilter ? (
            <>
              <span>30-day true cost: <strong style={{ color: '#15172b' }}>{fmtUsd(totalSpend)}</strong></span>
              <span style={{ color: '#888' }}>·</span>
              <span>With markup ({markup}×): <strong style={{ color: '#0F62FE' }}>{fmtUsd((Number(totalSpend) || 0) * markup)}</strong></span>
            </>
          ) : (
            <span>30-day total spend: <strong>{fmtUsd(totalSpend)}</strong></span>
          )}
        </div>

        {data?.projection && (
          <div className="text-muted" style={{ fontSize: 12, display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
            {isSuperAdmin && companyFilter ? (
              <>
                <span>Projected monthly true cost: <strong style={{ color: data.projection.projectedMonthlyUsd > 100 ? '#a71d2a' : '#1e6b3e' }}>{fmtUsd(data.projection.projectedMonthlyUsd)}</strong></span>
                <span style={{ color: '#888' }}>·</span>
                <span>With markup ({markup}×): <strong style={{ color: '#0F62FE' }}>{fmtUsd((Number(data.projection.projectedMonthlyUsd) || 0) * markup)}</strong></span>
                <span style={{ color: '#888' }}>· last 7d {fmtUsd(data.projection.trailing7TotalUsd)} ({data.projection.trailing7Calls} calls)</span>
              </>
            ) : (
              <>
                <span>Projected monthly burn (7d avg × 30): <strong style={{ color: data.projection.projectedMonthlyUsd > 100 ? '#a71d2a' : '#1e6b3e' }}>{fmtUsd(data.projection.projectedMonthlyUsd)}</strong></span>
                <span style={{ color: '#888' }}>· last 7d {fmtUsd(data.projection.trailing7TotalUsd)} ({data.projection.trailing7Calls} calls)</span>
              </>
            )}
          </div>
        )}
        {actionMsg && <span style={{ fontSize: 12, color: '#0F62FE' }}>{actionMsg}</span>}
      </div>
      {error && <div className="alert alert-warning" style={{ fontSize: 13 }}>{error}</div>}

      {/* COMPACT ANIMATED FLOW STRIP */}
      <div className="acc-card" style={{ padding: '6px 12px' }}>
        <div className="acc-flow-strip">
          {STAGE_FLOW.map((s, i) => {
            const q = (data && data.queues && data.queues[s.key]) || {};
            const inFlight = (q.waiting || 0) + (q.active || 0);
            return (
              <React.Fragment key={s.key}>
                <div className={"acc-flow-cell " + (stagePulse[s.key] ? "pulse" : "")}>
                  <div className="acc-flow-label">{s.label}</div>
                  <div className="acc-flow-num">{inFlight}</div>
                  <div style={{ fontSize: 10, color: '#aaa' }}>
                    {q.failed > 0 ? <span style={{ color: '#c0392b' }}>⚠ {q.failed} failed</span> : <>{q.completed || 0} done</>}
                  </div>
                </div>
                {i < STAGE_FLOW.length - 1 && <div className="acc-flow-arrow">→</div>}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* LLM COST GATE */}
      <LlmBudgetPanel />
      {/* TERMINAL/OPERATOR PROVIDER SWITCH + LIVE ACTIVITY */}
      <TerminalLlmPanel brandOptions={brandOptions} />
      <LlmCostDashboard companyFilter={companyFilter} markup={markup} />

      {/* PER-BRAND SPEND */}
      <div className="acc-card">
        <h5>Brands — 30-day spend & in-flight</h5>
        <table className="acc-table">
          <thead>
            <tr>
              <th>Brand</th>
              <th>$ spent</th>
              <th>Calls</th>
              <th>Tokens in</th>
              <th>Tokens out</th>
              <th>Posts</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(data?.perBrand || []).length === 0 && <tr><td colSpan="7" style={{ color:'#999' }}>No brand activity in the last 30 days.</td></tr>}
            {(data?.perBrand || []).map((b) => (
              <tr key={b.brandId}>
                <td>
                  <span className="acc-pick" onClick={() => setSelectedBrandId(b.brandId)}>{b.brandName}</span>
                </td>
                <td><strong>{fmtUsd(b.totalUsd)}</strong></td>
                <td>{b.totalCalls}</td>
                <td>{b.totalTokensIn}</td>
                <td>{b.totalTokensOut}</td>
                <td>{b.posts}</td>
                <td>
                  <button className="acc-cancel" onClick={() => cancelBrand(b.brandId)}>Cancel all in-flight</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* PER-STAGE COST BREAKDOWN */}
      <div className="acc-card">
        <h5>Cost by stage & model — 30 days</h5>
        <table className="acc-table">
          <thead>
            <tr>
              <th>Stage</th>
              <th>Model</th>
              <th>$ spent</th>
              <th>Calls</th>
              <th>Tokens in</th>
              <th>Tokens out</th>
            </tr>
          </thead>
          <tbody>
            {(data?.perStage || []).length === 0 && <tr><td colSpan="6" style={{ color:'#999' }}>No call data yet.</td></tr>}
            {(data?.perStage || []).map((s, i) => (
              <tr key={i}>
                <td>{s.kind || '?'}</td>
                <td>{s.model || '?'}</td>
                <td><strong>{fmtUsd(s.usd)}</strong></td>
                <td>{s.calls}</td>
                <td>{s.tokensIn}</td>
                <td>{s.tokensOut}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* PLATFORM LLM COSTS — super-admin only. Shows per-provider
          spend across ALL tenants + API-key configuration audit so the
          operator can see at a glance which keys are wired and where
          the platform's actual $ is going. data.perProvider + .apiKeysStatus
          are returned empty for companyAdmin (backend gates by req.scope.isSuper). */}
      {isSuperAdmin && (data?.perProvider?.length > 0 || data?.apiKeysStatus?.length > 0) && (
        <div className="acc-card">
          <h5 style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Platform LLM Costs — all tenants (30d)</span>
            <span style={{ fontSize: 11, fontWeight: 400, color: '#888', textTransform: 'none', letterSpacing: 0 }}>
              Total: <strong style={{ color: '#0F62FE' }}>{fmtUsd(data?.totalPlatformEstUsd || 0)}</strong>
            </span>
          </h5>

          {/* API key audit pills */}
          {(data?.apiKeysStatus || []).length > 0 && (
            <div style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {data.apiKeysStatus.map((k) => (
                <span key={k.provider}
                  className="acc-pill"
                  title={`${k.envVar} · ${k.family}${k.observedSpending ? ' · spending observed' : ''}`}
                  style={{
                    background: k.configured ? (k.observedSpending ? '#e7f5ec' : '#eef2ff') : (k.observedSpending ? '#fdecea' : '#f1f1f1'),
                    color:      k.configured ? (k.observedSpending ? '#1e6b3e' : '#15172b') : (k.observedSpending ? '#a71d2a' : '#525252'),
                  }}>
                  <strong>{k.provider}</strong> {k.configured ? '✓' : '✗ no key'} {k.observedSpending ? ' · live' : ''}
                </span>
              ))}
            </div>
          )}

          {(data?.perProvider || []).length === 0 && (
            <div className="text-muted" style={{ fontSize: 12, padding: '8px 0' }}>No spend in window.</div>
          )}
          {(data?.perProvider || []).length > 0 && (
            <table className="acc-table">
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Model</th>
                  <th>$ 30d</th>
                  <th>Calls</th>
                  <th>Tokens in</th>
                  <th>Tokens out</th>
                  <th>$ / call</th>
                </tr>
              </thead>
              <tbody>
                {data.perProvider.map((p, i) => (
                  <tr key={i}>
                    <td><strong>{p.provider || '?'}</strong></td>
                    <td>{p.model || '?'}</td>
                    <td><strong>{fmtUsd(p.usd)}</strong></td>
                    <td>{p.calls}</td>
                    <td>{p.tokensIn}</td>
                    <td>{p.tokensOut}</td>
                    <td>{fmtUsd(p.calls > 0 ? p.usd / p.calls : 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="text-muted" style={{ fontSize: 11, marginTop: 6 }}>
            Provider pills: <strong>green</strong> = key configured + actively spending · <strong>blue</strong> = configured but unused ·{' '}
            <strong>red</strong> = spending shown but key missing (legacy or misconfigured) · <strong>grey</strong> = not wired.
          </div>
        </div>
      )}

      {/* SPEND OVER TIME */}
      <div className="acc-card">
        <h5 style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Spend over time — daily $ stacked by kind</span>
          <span style={{ display: 'inline-flex', gap: 4, fontSize: 11, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
            {[7, 14, 30, 60].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setTsDays(d)}
                style={{
                  fontSize: 11, padding: '2px 8px', borderRadius: 12, cursor: 'pointer',
                  border: tsDays === d ? '1px solid #0F62FE' : '1px solid #ddd',
                  background: tsDays === d ? '#0F62FE' : '#fff',
                  color: tsDays === d ? '#fff' : '#525252',
                }}
              >
                {d}d
              </button>
            ))}
          </span>
        </h5>
        <SpendChart series={timeseries?.series} height={220} />
        <div className="text-muted" style={{ fontSize: 11, marginTop: 4 }}>
          Per-call cost timeline aggregated across all brands. Bars are bucketed by aiCall.ts (call timestamp, not post creation).
        </div>
      </div>

      {/* QUEUE & JOB CONTROL */}
      <div className="acc-card">
        <h5>Queue & job control</h5>
        <div className="text-muted" style={{ fontSize: 11, marginBottom: 8 }}>
          Click a queue row to expand: per-state job lists with Retry/Remove/Promote, plus Add-Job. Every mutation lands in the Manual-actions ledger below with an estimated USD impact.
        </div>
        {data && Object.entries(data.queues || {}).map(([short, q]) => (
          <QueueControlPanel
            key={short}
            queueName={q.name}
            totals={q}
            isSuperAdmin={isSuperAdmin}
            onActionDone={(msg) => { setActionMsg(msg); setTimeout(() => setActionMsg(null), 3500); fetchOverview(); }}
            onError={(msg) => { setActionMsg(msg); setTimeout(() => setActionMsg(null), 5000); }}
          />
        ))}
      </div>

      {/* MANUAL EVENT EMIT — super-admin only (the /admin/emit-event
          route is super-gated server-side; hiding here so company
          admins don't see a non-functional form). */}
      {isSuperAdmin && (
      <div className="acc-card">
        <h5>Manual trigger — emit outbox event</h5>
        <div className="text-muted" style={{ fontSize: 11, marginBottom: 8 }}>
          Drops an event onto the outbox bus. Use for re-running enrichment, forcing token refresh, or kicking off ideas outside the scheduler's slots. Pipeline events (POST_TEXT_READY, IDEA_POSTED, etc.) are blocked server-side — they must come from the upstream processor.
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-start' }}>
          <select
            value={emitEvt}
            onChange={(e) => onEmitEvtChange(e.target.value)}
            style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #ddd', borderRadius: 4, minWidth: 240 }}
          >
            {EMITTABLE_EVENTS.map((e) => (
              <option key={e.type} value={e.type}>{e.type} — {e.label}</option>
            ))}
          </select>
          <select
            value={emitBrandId}
            onChange={(e) => setEmitBrandId(e.target.value)}
            style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #ddd', borderRadius: 4, minWidth: 180 }}
          >
            <option value="">— pick brand —</option>
            {brandOptions.map((b) => (
              <option key={b._id} value={b._id}>{b.name}</option>
            ))}
          </select>
          <textarea
            value={emitPayload}
            onChange={(e) => setEmitPayload(e.target.value)}
            rows={2}
            placeholder='payload JSON e.g. {"count":1}'
            style={{ flex: '1 1 200px', minWidth: 180, fontSize: 12, padding: 6, border: '1px solid #ddd', borderRadius: 4, fontFamily: 'monospace' }}
          />
          <button
            type="button"
            onClick={submitEmit}
            disabled={emitBusy || !emitBrandId}
            style={{
              fontSize: 12, padding: '6px 14px', borderRadius: 4, cursor: emitBusy ? 'wait' : 'pointer',
              border: '1px solid #0F62FE', background: emitBusy ? '#7da4f0' : '#0F62FE', color: '#fff',
            }}
          >
            {emitBusy ? 'Emitting…' : 'Emit event'}
          </button>
        </div>
      </div>
      )}

      {/* SCHEDULERS */}
      <div className="acc-card">
        <h5>Schedulers</h5>
        <table className="acc-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Interval</th>
              <th>Last run</th>
              <th>Next run</th>
              <th>State</th>
              <th>Edit</th>
            </tr>
          </thead>
          <tbody>
            {(data?.schedulers || []).map((s) => (
              <tr key={s.name}>
                <td>{s.name}</td>
                <td>{s.intervalMs ? `${Math.round(s.intervalMs/1000)}s` : '—'}</td>
                <td>{fmtRel(s.lastRunAt)}</td>
                <td>{fmtRel(s.nextRunAt)}</td>
                <td>{s.paused ? <span className="acc-pill">paused</span> : <span className="acc-pill acc-pill-ok">running</span>}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {isSuperAdmin ? (
                    <>
                      <SchedulerIntervalEdit
                        name={s.name}
                        currentMs={s.intervalMs}
                        onSaved={() => { fetchOverview(); setActionMsg(`✓ ${s.name} interval updated`); setTimeout(() => setActionMsg(null), 3000); }}
                        onError={(msg) => { setActionMsg(`✗ ${msg}`); setTimeout(() => setActionMsg(null), 4000); }}
                      />
                      <span style={{ marginLeft: 8, display: 'inline-flex', gap: 4 }}>
                        {s.paused
                          ? <button className="acc-btn" onClick={() => schedulerAction(s.name, 'resume')}>Resume</button>
                          : <button className="acc-btn" onClick={() => schedulerAction(s.name, 'pause')}>Pause</button>}
                        <button className="acc-btn acc-btn-primary" onClick={() => schedulerAction(s.name, 'run-now')}>Run now</button>
                      </span>
                    </>
                  ) : (
                    <span className="text-muted" style={{ fontSize: 11 }} title="Schedulers are process-wide; super only">read-only</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="text-muted" style={{ fontSize: 11, marginTop: 6 }}>
          Interval edits are in-memory and reset on worker restart. For permanent changes, edit the scheduler factory defaults.
          Direct control: <code>POST /worker/schedulers/:name/pause|resume|run-now</code>.
        </div>
      </div>

      {/* WORKFLOWS */}
      <div className="acc-card">
        <h5>Active workflows — brand membership</h5>
        {(data?.workflows || []).map((w) => (
          <div key={w.key} style={{ marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid #f1f1f1' }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{w.label} <span className="text-muted" style={{ fontWeight: 400, fontSize: 11 }}>({w.brands.length} brand{w.brands.length === 1 ? '' : 's'})</span></div>
            <div className="text-muted" style={{ fontSize: 11, margin: '2px 0 6px' }}>{w.description}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {w.brands.length === 0 && <span style={{ fontSize: 11, color: '#bbb' }}>No brands.</span>}
              {w.brands.map((b) => (
                <span key={b._id} className="acc-pill" style={{ background: b.autopost ? '#e7f5ec' : '#f1f1f1', color: b.autopost ? '#1e6b3e' : '#525252' }} title={`tz=${b.timezone}; autopost=${b.autopost}`}>
                  {b.name}{!b.autopost && ' (paused)'}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* RECENT AI CALLS */}
      <div className="acc-card">
        <h5>Recent AI calls — last 100 <span className="text-muted" style={{ fontSize: 11, fontWeight: 'normal' }}>(click a row to expand input/output)</span></h5>
        <table className="acc-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Brand</th>
              <th>Kind</th>
              <th>Model</th>
              <th>$</th>
              <th>Tokens in/out</th>
              <th>Input / Output</th>
            </tr>
          </thead>
          <tbody>
            {(data?.recentCalls || []).length === 0 && <tr><td colSpan="7" style={{color:'#999'}}>No calls yet.</td></tr>}
            {(data?.recentCalls || []).map((c, i) => {
              const isExpanded = !!expandedCall[i];
              const previewClass = "acc-preview" + (isExpanded ? " expanded" : "");
              return (
                <tr key={i} className="acc-call-row" onClick={() => setExpandedCall((p) => ({ ...p, [i]: !p[i] }))} style={{ cursor: 'pointer' }}>
                  <td>{fmtRel(c.ts)}</td>
                  <td>{c.brandName || c.brandId}</td>
                  <td>{c.kind}</td>
                  <td>{c.model}</td>
                  <td><strong>{fmtUsd(c.usd)}</strong></td>
                  <td className="text-muted" style={{fontSize:11}}>{c.inTokens}/{c.outTokens}</td>
                  <td>
                    <div className={previewClass}>
                      {(c.inputPreview || c.outputPreview)
                        ? <>
                            {c.inputPreview && <div><strong>in:</strong> {c.inputPreview}</div>}
                            {c.outputPreview && <div><strong>out:</strong> {c.outputPreview}</div>}
                          </>
                        : <span style={{ color: '#bbb' }}>(no preview captured)</span>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* MANUAL ACTIONS LEDGER */}
      <div className="acc-card">
        <h5 style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Manual actions — last 100</span>
          <span style={{ fontSize: 11, fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#888' }}>
            30-day estimated manual spend: <strong style={{ color: '#0F62FE' }}>{fmtUsd(data?.totalManualEstUsd || 0)}</strong>
          </span>
        </h5>
        {(data?.costByAction || []).length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
            {data.costByAction.map((r) => (
              <span key={r.action} className="acc-pill" style={{ background: '#eef2ff', color: '#15172b' }} title={`${r.count} action${r.count === 1 ? '' : 's'}`}>
                {r.action}: <strong>{fmtUsd(r.estUsd)}</strong> <span style={{ color: '#888', fontSize: 10 }}>× {r.count}</span>
              </span>
            ))}
          </div>
        )}
        <table className="acc-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Action</th>
              <th>Target</th>
              <th>Brand / Queue</th>
              <th>Est $</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {(data?.recentAuditActions || []).length === 0 && <tr><td colSpan="6" style={{ color: '#999' }}>No manual actions yet.</td></tr>}
            {(data?.recentAuditActions || []).map((a) => (
              <tr key={a._id}>
                <td className="text-muted" style={{ fontSize: 11 }}>{fmtRel(a.createdAt)}</td>
                <td><span className="acc-pill" style={{ background: '#f1f1f1' }}>{a.action}</span> {a.eventType && <span className="text-muted" style={{ fontSize: 11 }}>({a.eventType})</span>}</td>
                <td style={{ fontFamily: 'monospace', fontSize: 11, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={a.target}>{a.target}</td>
                <td style={{ fontSize: 11 }}>
                  {a.brandId   && <span>brand:{a.brandId.slice(-6)} </span>}
                  {a.queueName && <span className="text-muted">{a.queueName}</span>}
                  {a.schedulerName && <span className="text-muted">sched:{a.schedulerName}</span>}
                </td>
                <td>{a.estUsd > 0 ? <strong>{fmtUsd(a.estUsd)}</strong> : <span style={{ color: '#bbb' }}>—</span>}</td>
                <td className="text-muted" style={{ fontSize: 11, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={a.notes}>{a.notes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* BRAND DRILL-IN */}
      {selectedBrandId && (
        <div className="acc-card">
          <h5>
            Brand drill-in: {brandDetail?.brand?.name || '...'}
            <span style={{ float: 'right' }}>
              <button className="acc-cancel" onClick={() => setSelectedBrandId(null)}>Close</button>
            </span>
          </h5>
          {brandDetail && (
            <>
              <div style={{ fontSize: 13, marginBottom: 8 }}>
                30-day: <strong>{fmtUsd(brandDetail.totals?.usd)}</strong> across {brandDetail.totals?.calls} calls
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Daily spend ({tsDays}d)</div>
                <SpendChart series={brandSeries?.series} height={160} compact />
              </div>
              {Object.entries(brandDetail.inFlight || {}).map(([short, q]) => (
                <div key={short} style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                    {short} — {(q.mine || []).length} job{q.mine?.length === 1 ? '' : 's'} for this brand
                    {(q.mine || []).length > 0 && (
                      <button className="acc-cancel" style={{ marginLeft: 8 }} onClick={() => cancelBrand(selectedBrandId, q.name)}>Cancel these</button>
                    )}
                  </div>
                  {(q.mine || []).slice(0, 10).map((j) => (
                    <div key={j.id} style={{ fontSize: 11, color: '#525252', padding: '2px 0', borderBottom: '1px dashed #f1f1f1' }}>
                      {j.id} — attempts: {j.attempts} — {fmtTime(j.ts)}
                    </div>
                  ))}
                </div>
              ))}
              <div style={{ fontSize: 12, fontWeight: 600, marginTop: 14, marginBottom: 6 }}>Recent posts:</div>
              <table className="acc-table">
                <thead>
                  <tr><th>Title</th><th>Platforms</th><th>queueStatus</th><th>$</th><th>Calls</th><th>Updated</th></tr>
                </thead>
                <tbody>
                  {(brandDetail.recentPosts || []).map((p) => (
                    <tr key={p._id}>
                      <td>{(p.title || '').slice(0, 50) || '(untitled)'}</td>
                      <td>{(p.socialPlatforms || []).join(', ')}</td>
                      <td>{p.queueStatus}</td>
                      <td>{fmtUsd(p.aiCostUSD)}</td>
                      <td>{p.llmCallCount}</td>
                      <td>{fmtTime(p.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── LLM cost gate panel ─────────────────────────────────────────────
// Master enable + daily / monthly USD caps + per-kind + per-worker
// toggles. Reads/writes /api/admin/llm-budget. The worker's gate
// re-reads the budget every 10s (cache TTL) so changes here take
// effect within a few seconds.
function LlmBudgetPanel() {
  const [doc, setDoc] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [msg, setMsg] = React.useState(null);
  const [draft, setDraft] = React.useState(null);

  const refresh = React.useCallback(async () => {
    setError(null);
    try {
      const r = await get('/api/admin/llm-budget');
      const d = (r && r.data) || null;
      setDoc(d);
      setDraft({
        enabled: d?.enabled !== false,
        dailyUsdCap: d?.dailyUsdCap ?? 5,
        monthlyUsdCap: d?.monthlyUsdCap ?? 50,
        perKind: Array.isArray(d?.perKind) ? d.perKind : [],
        perWorker: Array.isArray(d?.perWorker) ? d.perWorker : [],
      });
    } catch (e) { setError((e && e.message) || 'Failed to load'); }
  }, []);
  React.useEffect(() => { refresh(); }, [refresh]);
  React.useEffect(() => { const t = setInterval(refresh, 15_000); return () => clearInterval(t); }, [refresh]);

  const save = async (override) => {
    if (!draft) return;
    setBusy(true); setError(null); setMsg(null);
    try {
      const { putGeneral } = await import('../../api');
      await putGeneral('/api/admin/llm-budget', override || draft);
      setMsg('Saved — gate refreshes within ~10s');
      setTimeout(() => setMsg(null), 3000);
      refresh();
    } catch (e) { setError((e && e.message) || 'Save failed'); }
    finally { setBusy(false); }
  };

  const panic = async () => {
    if (!window.confirm('Kill all LLM calls for this company immediately?')) return;
    setBusy(true); setError(null);
    try {
      await post('/api/admin/llm-budget/panic', { reason: 'manual panic' }, false);
      refresh();
      setMsg('Master switch OFF — every generate() and embed() now denies');
    } catch (e) { setError((e && e.message) || 'Panic failed'); }
    finally { setBusy(false); }
  };

  if (!doc || !draft) {
    return (
      <div className="acc-card">
        <h5>LLM cost gate</h5>
        {error ? <div className="alert alert-warning" style={{ fontSize: 13 }}>{error}</div> : <div style={{ fontSize: 12, color: '#888' }}>Loading…</div>}
      </div>
    );
  }

  const dayPct = (doc.dailyUsdCap > 0) ? Math.min(100, (doc.todayUsd / doc.dailyUsdCap) * 100) : 0;
  const monthPct = (doc.monthlyUsdCap > 0) ? Math.min(100, (doc.monthUsd / doc.monthlyUsdCap) * 100) : 0;
  const barColor = (pct) => pct >= 100 ? '#a71d2a' : pct >= 80 ? '#d97706' : '#1e6b3e';

  return (
    <div className="acc-card">
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <h5 style={{ marginBottom: 0 }}>LLM cost gate</h5>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            background: doc.enabled ? '#e7f5ec' : '#fdecea',
            color: doc.enabled ? '#1e6b3e' : '#a71d2a',
            padding: '3px 12px', borderRadius: 12, fontSize: 11, fontWeight: 700,
          }}>
            {doc.enabled ? '✓ Active' : '⏸ MASTER OFF'}
          </span>
          {doc.enabled
            ? <button className="acc-btn" style={{ fontSize: 11, color: '#a71d2a', borderColor: '#a71d2a' }} onClick={panic} disabled={busy}>Panic stop</button>
            : <button className="acc-btn acc-btn-primary" onClick={() => save({ ...draft, enabled: true })} disabled={busy}>Resume</button>}
        </div>
      </div>
      {!doc.enabled && doc.lastDisabledReason && (
        <div style={{ background: '#fdecea', color: '#a71d2a', padding: 8, fontSize: 12, borderRadius: 4, marginBottom: 10 }}>
          Reason: {doc.lastDisabledReason}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Today's burn</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{fmtUsd(doc.todayUsd)} <span style={{ fontSize: 12, color: '#888' }}>/ {fmtUsd(doc.dailyUsdCap || 0)}</span></div>
          <div style={{ height: 6, background: '#e5e5e5', borderRadius: 3, marginTop: 6, overflow: 'hidden' }}>
            <div style={{ width: dayPct + '%', height: '100%', background: barColor(dayPct) }} />
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>This month</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{fmtUsd(doc.monthUsd)} <span style={{ fontSize: 12, color: '#888' }}>/ {fmtUsd(doc.monthlyUsdCap || 0)}</span></div>
          <div style={{ height: 6, background: '#e5e5e5', borderRadius: 3, marginTop: 6, overflow: 'hidden' }}>
            <div style={{ width: monthPct + '%', height: '100%', background: barColor(monthPct) }} />
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'end' }}>
        <label style={{ fontSize: 12 }}>
          Daily cap (USD)
          <input
            type="number" min="0" step="0.5"
            value={draft.dailyUsdCap}
            onChange={(e) => setDraft({ ...draft, dailyUsdCap: Number(e.target.value) || 0 })}
            className="form-control" style={{ fontSize: 13, width: 100 }}
          />
        </label>
        <label style={{ fontSize: 12 }}>
          Monthly cap (USD)
          <input
            type="number" min="0" step="5"
            value={draft.monthlyUsdCap}
            onChange={(e) => setDraft({ ...draft, monthlyUsdCap: Number(e.target.value) || 0 })}
            className="form-control" style={{ fontSize: 13, width: 100 }}
          />
        </label>
        <button className="acc-btn acc-btn-primary" onClick={() => save()} disabled={busy}>{busy ? 'Saving…' : 'Save caps'}</button>
        {msg && <span style={{ fontSize: 11, color: '#1e6b3e' }}>{msg}</span>}
        {error && <span style={{ fontSize: 11, color: '#a71d2a' }}>{error}</span>}
      </div>

      <details>
        <summary style={{ fontSize: 12, color: '#525252', cursor: 'pointer', marginBottom: 6 }}>
          Per-worker controls — disable individual processors without flipping the master switch
        </summary>
        <PerWorkerTable
          rows={draft.perWorker}
          onChange={(perWorker) => save({ ...draft, perWorker })}
          busy={busy}
        />
      </details>
    </div>
  );
}

// ── Terminal / operator provider switch + live activity ─────────────
// Flips the global providerMode (normal / terminal / auto) and shows
// "who's processing now" — the in-flight LlmTasks a terminal operator
// drains via /api/admin/llm/tasks. Reads /api/admin/llm/{mode,activity}.
// The worker's terminalProvider re-reads the mode every ~10s.
const MODE_DEFS = [
  { key: 'normal',   label: 'Normal',   desc: 'Real providers only', color: '#1e6b3e', bg: '#e7f5ec' },
  { key: 'auto',     label: 'Auto',     desc: 'Providers → operator fallback', color: '#856404', bg: '#fff4cc' },
  { key: 'terminal', label: 'Terminal', desc: 'Operator handles every call', color: '#0F62FE', bg: '#eef2ff' },
];
function TerminalLlmPanel({ brandOptions = [] }) {
  const [mode, setMode] = React.useState(null);
  const [act, setAct] = React.useState(null);
  const [overrides, setOverrides] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  // Add-override form state.
  const [obBrand, setObBrand] = React.useState('');
  const [obMode, setObMode] = React.useState('terminal');
  const [obKinds, setObKinds] = React.useState(''); // "post=terminal, email=auto"

  const refresh = React.useCallback(async () => {
    try {
      const [a, m] = await Promise.all([
        get('/api/admin/llm/activity'),
        get('/api/admin/llm/mode'),
      ]);
      const d = (a && a.data) || null;
      setAct(d);
      if (d && d.providerMode) setMode(d.providerMode);
      setOverrides((m && m.data && m.data.brandOverrides) || []);
      setErr(null);
    } catch (e) { setErr((e && e.message) || 'Failed to load'); }
  }, []);
  React.useEffect(() => { refresh(); const t = setInterval(refresh, 5000); return () => clearInterval(t); }, [refresh]);

  const switchMode = async (m) => {
    if (m === mode) return;
    if (m === 'terminal' && !window.confirm('Route EVERY brand\'s LLM calls to the terminal operator queue (except brands with their own override)? Generation will wait for an operator to drain it.')) return;
    setBusy(true); setErr(null);
    try {
      await post('/api/admin/llm/mode', { providerMode: m }, false);
      setMode(m);
      refresh();
    } catch (e) { setErr((e && e.message) || 'Switch failed'); }
    finally { setBusy(false); }
  };

  const parseKinds = (s) => String(s || '').split(',').map((p) => p.trim()).filter(Boolean)
    .map((p) => { const [k, v] = p.split('='); return { kind: (k || '').trim(), providerMode: (v || '').trim() }; })
    .filter((x) => x.kind && ['normal', 'terminal', 'auto'].includes(x.providerMode));

  const saveOverride = async () => {
    if (!obBrand) { setErr('Pick a brand'); return; }
    setBusy(true); setErr(null);
    try {
      const name = (brandOptions.find((b) => b._id === obBrand) || {}).name || '';
      const { putGeneral } = await import('../../api');
      await putGeneral(`/api/admin/llm/mode/brand/${obBrand}`, { providerMode: obMode, brandName: name, kindOverrides: parseKinds(obKinds) });
      setObBrand(''); setObKinds('');
      refresh();
    } catch (e) { setErr((e && e.message) || 'Save failed'); }
    finally { setBusy(false); }
  };

  const removeOverride = async (brandId) => {
    setBusy(true); setErr(null);
    try {
      const { remove } = await import('../../api');
      await remove(`/api/admin/llm/mode/brand/${brandId}`, {});
      refresh();
    } catch (e) { setErr((e && e.message) || 'Remove failed'); }
    finally { setBusy(false); }
  };

  const brandName = (id) => (brandOptions.find((b) => b._id === id) || {}).name || id;
  const cur = MODE_DEFS.find((d) => d.key === mode);
  return (
    <div className="acc-card">
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <h5 style={{ marginBottom: 0 }}>LLM provider mode — terminal/operator fallback</h5>
        {cur && (
          <span style={{ background: cur.bg, color: cur.color, padding: '3px 12px', borderRadius: 12, fontSize: 11, fontWeight: 700 }}>
            {cur.label}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {MODE_DEFS.map((d) => (
          <button
            key={d.key}
            type="button"
            disabled={busy}
            onClick={() => switchMode(d.key)}
            title={d.desc}
            style={{
              fontSize: 12, padding: '6px 14px', borderRadius: 6, cursor: busy ? 'wait' : 'pointer',
              border: mode === d.key ? `2px solid ${d.color}` : '1px solid #ddd',
              background: mode === d.key ? d.bg : '#fff',
              color: mode === d.key ? d.color : '#525252',
              fontWeight: mode === d.key ? 700 : 400,
            }}
          >
            {d.label}<span style={{ display: 'block', fontSize: 10, fontWeight: 400, color: '#888' }}>{d.desc}</span>
          </button>
        ))}
      </div>
      {err && <div className="alert alert-warning" style={{ fontSize: 12 }}>{err}</div>}

      <div style={{ display: 'flex', gap: 16, fontSize: 12, marginBottom: 10 }}>
        <span>Pending: <strong style={{ color: (act?.pending || 0) > 0 ? '#0F62FE' : '#15172b' }}>{act?.pending || 0}</strong></span>
        <span>Claimed: <strong>{act?.claimed || 0}</strong></span>
        <span className="text-muted">Done: {act?.counts?.done || 0} · Failed: {act?.counts?.failed || 0}</span>
      </div>

      {/* PER-BRAND OVERRIDES */}
      <div style={{ borderTop: '1px solid #eee', marginTop: 6, paddingTop: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
          Per-brand overrides <span className="text-muted" style={{ fontWeight: 400 }}>— route specific brands (and features) differently from the global mode above</span>
        </div>
        {overrides.length > 0 && (
          <table className="acc-table" style={{ marginBottom: 8 }}>
            <thead><tr><th>Brand</th><th>Mode</th><th>Per-feature</th><th></th></tr></thead>
            <tbody>
              {overrides.map((o) => (
                <tr key={o.brandId}>
                  <td>{o.brandName || brandName(o.brandId)}</td>
                  <td><span className="acc-pill" style={{ background: '#eef2ff', color: '#15172b' }}>{o.providerMode}</span></td>
                  <td className="text-muted" style={{ fontSize: 11 }}>
                    {(o.kindOverrides || []).length ? o.kindOverrides.map((k) => `${k.kind}=${k.providerMode}`).join(', ') : '—'}
                  </td>
                  <td><button className="acc-btn acc-btn-danger" disabled={busy} onClick={() => removeOverride(o.brandId)}>Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 12 }}>
          <select value={obBrand} onChange={(e) => setObBrand(e.target.value)} style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #ddd', borderRadius: 4, minWidth: 180 }}>
            <option value="">— pick brand —</option>
            {brandOptions.map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
          </select>
          <select value={obMode} onChange={(e) => setObMode(e.target.value)} style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #ddd', borderRadius: 4 }}>
            {['terminal', 'auto', 'normal', 'inherit'].map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <input
            type="text" value={obKinds} onChange={(e) => setObKinds(e.target.value)}
            placeholder="per-feature (optional): post=terminal, email=auto"
            style={{ flex: '1 1 240px', minWidth: 200, fontSize: 12, padding: '4px 8px', border: '1px solid #ddd', borderRadius: 4 }}
          />
          <button className="acc-btn acc-btn-primary" disabled={busy || !obBrand} onClick={saveOverride}>Save override</button>
        </div>
      </div>

      <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>Who's processing now (in-flight LLM tasks)</div>
      <table className="acc-table">
        <thead>
          <tr><th>Kind</th><th>Worker</th><th>Brand</th><th>Status</th><th>Operator</th><th>Age</th><th>Attempts</th></tr>
        </thead>
        <tbody>
          {(!act || (act.inflight || []).length === 0) && (
            <tr><td colSpan="7" style={{ color: '#999' }}>Nothing in flight{mode === 'normal' ? ' (real providers handle calls directly).' : '.'}</td></tr>
          )}
          {(act?.inflight || []).map((t) => (
            <tr key={t._id}>
              <td>{t.kind || '—'}</td>
              <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{t.worker || '—'}</td>
              <td className="text-muted" style={{ fontSize: 11 }}>{t.brand_id ? String(t.brand_id).slice(-6) : '—'}</td>
              <td><span className="acc-pill" style={{ background: t.status === 'claimed' ? '#eef2ff' : '#fff4cc', color: t.status === 'claimed' ? '#0F62FE' : '#856404' }}>{t.status}</span></td>
              <td className="text-muted" style={{ fontSize: 11 }}>{t.claimedBy || '—'}</td>
              <td className="text-muted" style={{ fontSize: 11 }}>{t.ageSeconds != null ? `${t.ageSeconds}s` : '—'}</td>
              <td>{t.attempts || 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="text-muted" style={{ fontSize: 11, marginTop: 6 }}>
        In <strong>terminal</strong> mode every generate()/vision call parks here and waits for an operator. A Claude Code terminal session drains the queue via <code>/api/admin/llm/tasks</code> (claim → complete). Switch back to <strong>normal</strong> once providers are funded. Mode changes apply within ~10s.
      </div>
    </div>
  );
}

function PerWorkerTable({ rows, onChange, busy }) {
  // All real processors that route LLM calls today.
  const KNOWN = [
    'emailClassificationProcessor',
    'emailReplyDraftProcessor',
    'crmStateAnalyzerProcessor',
    'crmCustomerActionProcessor',
    'crmProspectActionProcessor',
    'newsletterGenerationProcessor',
    'seoArticleGenerationProcessor',
    'seoBriefGenerationProcessor',
    'seoFaqAnswerProcessor',
    'seoKeywordResearchProcessor',
    'chatClassificationProcessor',
    'chatReplyDraftProcessor',
    'ideaGenerationProcessor',
    'postGenerationProcessor',
    'brandEnrichmentProcessor',
  ];
  const byName = new Map((rows || []).map((r) => [r.worker, r]));
  const next = KNOWN.map((w) => ({ worker: w, enabled: byName.has(w) ? byName.get(w).enabled !== false : true }));

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 6, marginTop: 6 }}>
      {next.map((w) => (
        <label key={w.worker} style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
          border: '1px solid #e5e5e5', borderRadius: 4, fontSize: 12,
          background: w.enabled ? '#fff' : '#fafbfc',
          opacity: w.enabled ? 1 : 0.7,
        }}>
          <input
            type="checkbox"
            checked={w.enabled}
            disabled={busy}
            onChange={(e) => {
              const updated = next.map((row) =>
                row.worker === w.worker ? { ...row, enabled: e.target.checked } : row);
              onChange(updated);
            }}
          />
          <span style={{ fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace', fontSize: 11 }}>{w.worker}</span>
        </label>
      ))}
    </div>
  );
}
